import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Country, State } from "country-state-city";

export default function FilterFeesComponent({
  clients,
  journals,
  publications,
  services,
  selectedClient,
  setSelectedClient,
  selectedJournal,
  setSelectedJournal,
  selectedPublication,
  setSelectedPublication,
  selectedCountry,
  setSelectedCountry,
  selectedState,
  setSelectedState,
  selectedService,
  setSelectedService,
  selectedCurrency,
  setSelectedCurrency,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  loadingOptions,
  onApply,
  loadingData,
}) {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountryName, setSelectedCountryName] = useState("");

  // Load all countries on mount
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);

  // Load states when country changes
  useEffect(() => {
    if (selectedCountry) {
      const countryStates = State.getStatesOfCountry(selectedCountry);
      setStates(countryStates);
    } else {
      setStates([]);
    }
  }, [selectedCountry]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onApply({
      selectedClient,
      selectedJournal,
      selectedPublication,
      selectedCountry: selectedCountryName,
      selectedState,
      selectedService,
      selectedCurrency,
      startDate,
      endDate,
    });
  };

  const handleClearFilters = () => {
    setSelectedClient("");
    setSelectedJournal("");
    setSelectedPublication("");
    setSelectedCountry("");
    setSelectedCountryName("");
    setSelectedState("");
    setSelectedService("");
    setSelectedCurrency("INR");
    setStartDate("");
    setEndDate("");
  };

  const handleCountryChange = (e) => {
    const countryIsoCode = e.target.value;
    setSelectedCountry(countryIsoCode);
    
    // Find and set the country name
    const selectedCountryObj = countries.find(c => c.isoCode === countryIsoCode);
    setSelectedCountryName(selectedCountryObj ? selectedCountryObj.name : "");
    
    // Clear state when country changes
    setSelectedState("");
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, margin: "auto" }}>
      {loadingOptions && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress />
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            mb: 2,
          }}
        >
          {/* Existing filters */}
          <FormControl fullWidth>
            <InputLabel id="client-label">Client</InputLabel>
            <Select
              labelId="client-label"
              value={selectedClient}
              label="Client"
              onChange={(e) => setSelectedClient(e.target.value)}
              disabled={loadingOptions}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.first_name} {c.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="journal-label">Journal</InputLabel>
            <Select
              labelId="journal-label"
              value={selectedJournal}
              label="Journal"
              onChange={(e) => setSelectedJournal(e.target.value)}
              disabled={loadingOptions}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {journals.map((j) => (
                <MenuItem key={j.journalid} value={j.journalName}>
                  {j.journalName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="publication-label">Publication</InputLabel>
            <Select
              labelId="publication-label"
              value={selectedPublication}
              label="Publication"
              onChange={(e) => setSelectedPublication(e.target.value)}
              disabled={loadingOptions}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {publications.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Service */}
          <FormControl fullWidth>
            <InputLabel id="service-label">Service</InputLabel>
            <Select
              labelId="service-label"
              value={selectedService}
              label="Service"
              onChange={(e) => setSelectedService(e.target.value)}
              disabled={loadingOptions}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {services.map((s) => (
                <MenuItem key={s.serviceId} value={s.serviceName}>
                  {s.serviceName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Country Dropdown */}
          <FormControl fullWidth>
            <InputLabel id="country-label">Country</InputLabel>
            <Select
              labelId="country-label"
              value={selectedCountry}
              label="Country"
              onChange={handleCountryChange}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.isoCode} value={country.isoCode}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* State Dropdown */}
          <FormControl fullWidth>
            <InputLabel id="state-label">State</InputLabel>
            <Select
              labelId="state-label"
              value={selectedState}
              label="State"
              onChange={(e) => setSelectedState(e.target.value)}
              disabled={!selectedCountry}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {states.map((state) => (
                <MenuItem key={state.isoCode} value={state.name}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Currency Dropdown */}
          <FormControl fullWidth>
            <InputLabel id="currency-label">Currency</InputLabel>
            <Select
              labelId="currency-label"
              value={selectedCurrency}
              label="Currency"
              onChange={(e) => setSelectedCurrency(e.target.value)}
            >
              <MenuItem value="INR">INR (₹)</MenuItem>
              <MenuItem value="USD">USD ($)</MenuItem>
              <MenuItem value="AED">AED (د.إ)</MenuItem>
            </Select>
          </FormControl>

          {/* Date range */}
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            disabled={loadingData || loadingOptions}
          >
            Clear Filters
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loadingData || loadingOptions}
          >
            {loadingData ? "Applying..." : "Apply"}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
